import ContentLoading from "component/LoadingComponent/ContentLoading";
import DataNotFound from "component/shared/DataNotFound/DataNotFound";
import { API_BASE_URL } from "configs/AppConfig";
import { END_POINTS } from "constants/APIConstant";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import React from "react";
import Modal from "react-bootstrap/Modal";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import "./MediaDetails.scss";
import LabelLang from "component/LabelLang/label-lang";
import { useIntl } from "react-intl";
import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";
import OwlCarousel from "react-owl-carousel";
import ReactGA from "react-ga4";

export default function MediaDetails() {
  const intl = useIntl();

  const { id } = useParams();

  const photoList = useSWR({
    url: id ? `${END_POINTS.PHOTO_ALBUM_DETAILS}?photoAlbumId=${id}` : null,
  });

  React.useEffect(() => {
    const timeOut = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => {
      clearTimeout(timeOut);
    };
  }, [id]);

  // preview
  const [imagePreview, setImagePreview] = React.useState("");
  const [show, setShow] = React.useState(false);
  const carouselRef = React.useRef(null);

  const handleClose = () => setShow(false);
  const handleShow = () => {
    setShow(true);
  };

  const options = {
    loop: false,
    center: true,
    margin: 0,
    responsiveClass: true,
    autoplay: false,
    dots: true,
    smartSpeed: 600,
    nav: true,
    items: 1,
    dir: "ltr",
    mouseDrag: true,
    touchDrag: true,
    pullDrag: true,
    freeDrag: false,
    stagePadding: 0,
    merge: false,
    mergeFit: true,
    autoWidth: false,
    startPosition: 0,
    URLhashListener: false,
    navText: ["‹", "›"],
    slideBy: 1,
    slideTransition: "linear",
    autoplayTimeout: 5000,
    autoplayHoverPause: true,
    animateOut: "fadeOut",
    animateIn: "fadeIn",
  };

  const renderPhotoAlbum = () => {
    const rendering = photoList?.data?.data?.photos
      ?.filter((el) => el.image !== imagePreview)
      .map(({ image }, i) => (
        <div key={i} className="item">
          <img
            // className="w-100"
            style={{ height: "400px", objectFit: "cover" }}
            src={
              image
                ? `${API_BASE_URL}/${image}`
                : process.env.PUBLIC_URL + "/image/newsr1.jpg"
            }
            alt="ved-item"
            loading="lazy"
          />
        </div>
      ));

    return rendering;
  };

  const renderPhotosList = () => {
    const photoos =
      photoList.error ||
      photoList.isLoading ||
      photoList.data === undefined ||
      photoList.data === null
        ? []
        : photoList.data.data.photos;

    const rendering = photoos.map(({ thumbnail, image }, i) => (
      <div key={i} className="col-12 col-md-6 col-lg-4 col-xl-3">
        <div className="text-center">
          <div className="img-item position-relative">
            <img
              className="img1"
              src={
                image
                  ? `${API_BASE_URL}/${image}`
                  : process.env.PUBLIC_URL + "/image/newsr1.png"
              }
              alt="ved-item"
              style={{ objectFit: "cover" }}
              loading="lazy"
            />

            <div
              className="preview"
              onClick={() => {
                setImagePreview(image);
                handleShow();
              }}
            >
              <span>{intl.formatMessage({ id: "preview" })}</span>
              <VisibilityOutlinedIcon />
            </div>
          </div>
        </div>
      </div>
    ));

    return rendering;
  };

  ReactGA.initialize(END_POINTS.TRACKING_ID);

  React.useEffect(() => {
    // Send pageview with a custom path
    ReactGA.send({
      hitType: "pageview",
      page: `/mediaLanding/media/${id}`,
      title: `Media details title: ${photoList?.data?.data?.photoAlbum?.title}`,
    });
  }, [id]);

  // Keyboard navigation for slider
  React.useEffect(() => {
    if (!show) return; // Only enable keyboard navigation when modal is open

    const getCarousel = () => {
      let carousel = null;

      // Try to get from ref first
      if (carouselRef.current) {
        const carouselEl = carouselRef.current;
        if (carouselEl?.carousel) carousel = carouselEl.carousel;
        if (carouselEl?.owlCarousel) carousel = carouselEl.owlCarousel;
      }

      // Try to get from DOM using jQuery (OwlCarousel uses jQuery)
      if (!carousel) {
        const sliderElement = document.querySelector("#album-details-slider");
        if (
          sliderElement &&
          window.$ &&
          window.$(sliderElement).data("owl.carousel")
        ) {
          carousel = window.$(sliderElement).data("owl.carousel");
        }
      }

      return carousel;
    };

    const handleKeyDown = (event) => {
      // Don't interfere with input fields
      const isInputFocused =
        document.activeElement?.tagName === "INPUT" ||
        document.activeElement?.tagName === "TEXTAREA";

      if (isInputFocused) return;

      // Get carousel instance
      const carousel = getCarousel();
      if (!carousel) {
        // Try again after a short delay if carousel is not ready
        setTimeout(() => {
          const retryCarousel = getCarousel();
          if (
            retryCarousel &&
            (event.key === "ArrowLeft" || event.key === "ArrowRight")
          ) {
            event.preventDefault();
            event.stopPropagation();
            try {
              if (event.key === "ArrowLeft") {
                retryCarousel.prev();
              } else if (event.key === "ArrowRight") {
                retryCarousel.next();
              }
            } catch (error) {
              console.error("Error navigating carousel:", error);
            }
          }
        }, 50);
        return;
      }

      // Handle arrow keys
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        event.stopPropagation();

        try {
          if (event.key === "ArrowLeft") {
            carousel.prev();
          } else if (event.key === "ArrowRight") {
            carousel.next();
          }
        } catch (error) {
          console.error("Error navigating carousel:", error);
        }
      }

      // Handle Escape key to close modal
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setShow(false);
      }
    };

    // Add event listener when modal is open
    // Use a delay to ensure carousel is initialized
    const timeoutId = setTimeout(() => {
      window.addEventListener("keydown", handleKeyDown);
    }, 200);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [show]);

  return (
    <ContentLoading
      isLoading={
        // photoList.error ||
        photoList.isLoading
      }
    >
      <div className="media-center">
        <div className="m__title__magazine">
          <h3>
            <LabelLang
              id="title"
              defaultValue={photoList?.data?.data?.photoAlbum?.title}
              translations={
                photoList?.data?.data?.photoAlbum?.photoAlbumTranslations
              }
            />
          </h3>
        </div>

        <div className="row gy-4 gx-0 gx-md-4">
          {Object.keys(renderPhotosList()).length > 0 ? (
            renderPhotosList()
          ) : (
            <DataNotFound />
          )}
        </div>
      </div>

      {/* Preview Modal - moved outside map to fix keyboard navigation */}
      <Modal
        style={{ zIndex: "9999999999" }}
        show={show}
        onHide={handleClose}
        centered
        size="lg"
      >
        <Modal.Body style={{ padding: "1.5rem", textAlign: "center" }}>
          <OwlCarousel
            ref={carouselRef}
            id="album-details-slider"
            className="owl-carousel owl-theme"
            {...options}
          >
            <img
              style={{
                height: "400px",
                objectFit: "cover",
              }}
              src={
                imagePreview
                  ? `${API_BASE_URL}/${imagePreview}`
                  : process.env.PUBLIC_URL + "/image/newsr1.png"
              }
              alt="ved-item"
              loading="lazy"
            />
            {renderPhotoAlbum()}
          </OwlCarousel>
        </Modal.Body>
      </Modal>
    </ContentLoading>
  );
}
